import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { Trash, PencilFill, FileEarmarkExcel, Printer } from 'react-bootstrap-icons';
import moment from 'moment';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import DateFilter from '@inovua/reactdatagrid-community/DateFilter';
import { jsPDF } from 'jspdf';
import XLSX from 'sheetjs-style';
import 'jspdf-autotable';
import 'assets/css/table.css';
import dateformat from 'dateformat';
import NavMenu from './navbar';

function MyAwesomeTable({ rows, isLoading, onDelete, setIds, setAdds }) {
    window.moment = moment;
    const [selected, setSelected] = useState({});
    const [gridRef, setGridRef] = useState(null);
    const [showFact, setShowFact] = useState(false);
    const [reportStartDate, setReportStartDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');

    const filterValue = [
        { name: 'codeinventaire', operator: 'contains', type: 'string', value: '' },
        { name: 'dateinventaire', operator: 'inrange', type: 'date', value: '' },
        { name: 'commentaire', operator: 'contains', type: 'string', value: '' },
        { name: 'titre', operator: 'contains', type: 'string', value: '' },
        { name: 'user', operator: 'contains', type: 'string', value: '' }
    ];

    const columnss = [
        {
            name: 'id',
            header: 'Id',
            defaultVisible: false,
            defaultWidth: 80,
            type: 'number'
        },

        {
            header: 'Action',
            defaultWidth: 90,
            type: 'string',
            render: ({ data }) => {
                return (
                    <div className="menudrops">
                        <ButtonGroup aria-label="Basic example">
                            <OverlayTrigger overlay={<Tooltip id="tooltip-disabled">Modifier ligne</Tooltip>}>
                                <a href="#/admin/inventaire" onClick={() => setIds(data.id)}>
                                    <PencilFill size={25} color="green" />
                                </a>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip id="tooltip-disabled">Supprimer ligne</Tooltip>}>
                                <a href="#/admin/inventaire" onClick={() => onDelete(data.id)}>
                                    <Trash color="red" size={25} />{' '}
                                </a>
                            </OverlayTrigger>
                        </ButtonGroup>
                    </div>
                );
            }
        },
        {
            name: 'codeinventaire',
            header: 'N° inventaire',
            defaultWidth: 100,
            type: 'string'
        },
        {
            name: 'dateinventaire',
            header: 'Date inventaire',
            defaultWidth: 200,
            type: 'date',
            textAlign: 'center',
            dateFormat: 'DD-MM-YYYY',
            filterEditor: DateFilter,
            filterEditorProps: (props, { index }) => {
                // for range and notinrange operators, the index is 1 for the after field
                return {
                    dateFormat: 'DD-MM-YYYY',
                    placeholder: index === 1 ? 'Date fin' : 'Date debut'
                };
            },
            render: ({ value, cellProps: { dateFormat } }) => moment(value).format(dateFormat)
        },
        {
            name: 'titre',
            header: 'Titre',
            defaultWidth: 200,
            type: 'string'
        },
        {
            name: 'commentaire',
            header: 'Commentaire',
            defaultWidth: 200,
            type: 'string'
        },
        { name: 'username', header: 'Crée par', defaultWidth: 100, type: 'string' },
        {
            name: 'created_at',
            header: 'Crée le',
            defaultWidth: 130,
            type: 'string',
            render: ({ value }) => dateformat(value, 'dd-mm-yyyy HH:MM')
        },
        {
            name: 'updated_at',
            header: 'Modif. le',
            defaultWidth: 130,
            type: 'string',
            render: ({ value }) => dateformat(value, 'dd-mm-yyyy HH:MM')
        }
    ];

    const getReportLines = () => {
        return rows
            .filter((inventory) => {
                const inventoryDate = String(inventory.dateinventaire).slice(0, 10);
                return (
                    (!reportStartDate || inventoryDate >= reportStartDate) &&
                    (!reportEndDate || inventoryDate <= reportEndDate)
                );
            })
            .flatMap((inventory) =>
                (Array.isArray(inventory.ligneinventaire) ? inventory.ligneinventaire : []).map((line) => {
                    const quantity = Number(line.pivot?.quantite_mouvement ?? line.qtediff ?? 0);
                    const price = Number(line.prixachat ?? 0);
                    return {
                        ...line,
                        dateinventaire: inventory.dateinventaire,
                        montant: price * quantity,
                        quantiteMouvement: quantity
                    };
                })
            );
    };

    const setFacts = () => {
        setShowFact(true);
    };

    const reportLines = getReportLines();
    const reportGroups = Object.values(
        reportLines.reduce((groups, line) => {
            const date = String(line.dateinventaire).slice(0, 10);
            const group = groups[date] || { date, positiveAmount: 0, negativeAmount: 0, lineCount: 0 };
            if (line.quantiteMouvement > 0) {
                group.positiveAmount += line.montant;
            } else if (line.quantiteMouvement < 0) {
                group.negativeAmount += line.montant;
            }
            group.lineCount += 1;
            groups[date] = group;
            return groups;
        }, {})
    );
    const positiveAmount = reportGroups.reduce((total, group) => total + group.positiveAmount, 0);
    const negativeAmount = reportGroups.reduce((total, group) => total + group.negativeAmount, 0);
    const reportResult = positiveAmount + negativeAmount;
    const formatAmount = (amount) => new Intl.NumberFormat().format(amount);
    
    const exportReportToExcel = () => {
        const reportRows = reportGroups.map((group) => ({
            date: moment(group.date).format('DD-MM-YYYY'),
            valeurPositive: group.positiveAmount,
            valeurNegative: group.negativeAmount,
            compteResultats: group.positiveAmount + group.negativeAmount,
            nombreLignes: group.lineCount
        }));
        reportRows.push({
            date: 'Total',
            valeurPositive: positiveAmount,
            valeurNegative: negativeAmount,
            compteResultats: reportResult,
            nombreLignes: reportLines.length
        });

        const worksheet = XLSX.utils.json_to_sheet(reportRows);
        XLSX.utils.sheet_add_aoa(
            worksheet,
            [['Date', 'Valeur positive', 'Valeur négative', 'Compte des résultats', 'Nombre de lignes']],
            { origin: 'A1' }
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Rapport inventaire');
        XLSX.writeFile(workbook, 'rapport-inventaire.xlsx');
    };

    const exportReportToPdf = () => {
        const doc = new jsPDF();
        const period = `${reportStartDate || 'Toutes dates'} - ${reportEndDate || 'Toutes dates'}`;
        const body = reportGroups.map((group) => [
            moment(group.date).format('DD-MM-YYYY'),
            formatAmount(group.positiveAmount),
            formatAmount(group.negativeAmount),
            formatAmount(group.positiveAmount + group.negativeAmount),
            group.lineCount
        ]);
        body.push(['Total', formatAmount(positiveAmount), formatAmount(negativeAmount), formatAmount(reportResult), reportLines.length]);

        doc.setFontSize(16);
        doc.text('RAPPORT INVENTAIRE', 14, 16);
        doc.setFontSize(10);
        doc.text(`Période : ${period}`, 14, 24);
        doc.autoTable({
            startY: 30,
            head: [['Date', 'Valeur positive', 'Valeur négative', 'Compte des résultats', 'Nombre de lignes']],
            body,
            theme: 'grid',
            styles: { halign: 'center', fontSize: 9 }
        });
        doc.save('rapport-inventaire.pdf');
    };

    const gridStyle = { minHeight: window.screen.height - 200 };

    const onSelectionChange = useCallback(({ selected }) => {
        setSelected(selected);
    }, []);

    const toArray = (selected) => Object.keys(selected).map((id) => id * 1);

    return (
        <div>
            <NavMenu selected={JSON.stringify(toArray(selected))} setAdds={setAdds} setFacts={setFacts} />
            <ReactDataGrid
                idProperty="id"
                handle={setGridRef}
                style={gridStyle}
                loading={isLoading}
                reorderColumns={false}
                pagination
                selected={selected}
                // checkboxColumn
                onSelectionChange={onSelectionChange}
                pageSizes={[200, 400, 600, 800, 1000]}
                defaultLimit={400}
                showHoverRows={true}
                editable={true}
                showColumnMenuTool={false}
                defaultFilterValue={filterValue}
                columns={columnss}
                dataSource={rows}
            />
            <Modal show={showFact} onHide={() => setShowFact(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Rapport inventaire</Modal.Title>
                    <div className="d-flex gap-2 ms-auto me-3">
                        <Button variant="success" onClick={exportReportToExcel}>
                            <FileEarmarkExcel className="me-2" /> Excel
                        </Button>
                        <Button variant="primary" onClick={exportReportToPdf}>
                            <Printer className="me-2" /> PDF
                        </Button>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex gap-3 mb-3">
                        <Form.Group controlId="reportStartDate">
                            <Form.Label>Du</Form.Label>
                            <Form.Control
                                type="date"
                                value={reportStartDate}
                                onChange={(event) => setReportStartDate(event.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="reportEndDate">
                            <Form.Label>Au</Form.Label>
                            <Form.Control
                                type="date"
                                value={reportEndDate}
                                onChange={(event) => setReportEndDate(event.target.value)}
                            />
                        </Form.Group>
                    </div>
                    <Table striped bordered responsive>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Valeur positive</th>
                                <th>Valeur négative</th>
                                <th>Compte des résultats</th>
                                <th>Nombre de lignes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportGroups.map((group) => (
                                <tr key={group.date}>
                                    <td>{moment(group.date).format('DD-MM-YYYY')}</td>
                                    <td>{formatAmount(group.positiveAmount)}</td>
                                    <td>{formatAmount(group.negativeAmount)}</td>
                                    <td>{formatAmount(group.positiveAmount + group.negativeAmount)}</td>
                                    <td>{group.lineCount}</td>
                                </tr>
                            ))}
                            <tr>
                                <th>Total</th>
                                <th>{formatAmount(positiveAmount)}</th>
                                <th>{formatAmount(negativeAmount)}</th>
                                <th>{formatAmount(reportResult)}</th>
                                <th>{reportLines.length}</th>
                            </tr>
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default MyAwesomeTable;
